import styled from "@emotion/styled"
import Typography from "@mui/material/Typography"
import { breakpoints } from "../../theme/mediaQueries"
import { sizes } from "../../theme/sizes"
import { colors } from "../../theme/colors"

/* ------------------------------------------------------------------ */
/* Root                                                                 */
/* ------------------------------------------------------------------ */
export const Root = styled.div`
  width: 100%;
  max-width: 100%;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${sizes.spacing[32]}px;
  padding: ${sizes.spacing[40]}px 0;

  @media (min-width: ${breakpoints.lg}px) {
    gap: ${sizes.spacing[48]}px;
    padding: ${sizes.spacing[64]}px 0;
  }

  .MuiTabs-root {
    width: 100%;
    max-width: 100%;
    /* EGDE-274: the theme draws the divider on .MuiTabs-flexContainer, which is
       width:fit-content (required so the row can scroll). In RTL it hugged the
       tabs on the right and stopped short of the strip ends / scroll-button
       chevrons. Keep that border (the active tab's orange underline overlaps it
       via a -2px margin and must not be clipped) and add a full-width rule
       BEHIND the strip so the gray line reaches end to end, under the chevrons. */
    position: relative;
    isolation: isolate;

    &::after {
      content: "";
      position: absolute;
      inset-inline: 0;
      bottom: 0;
      height: 2px;
      background: ${colors.primitives.neutral300};
      z-index: -1;
    }
  }

  /* EGDE-274: MUI's default Tab min-width (72–90px) padded short category
     labels into wide, uneven gaps. Let each tab hug its label so only the
     flexContainer gap (16px) separates them. */
  .MuiTab-root {
    min-width: auto;
  }
`

/* ------------------------------------------------------------------ */
/* Inner Hero — title + subtitle + tabs                                */
/* ------------------------------------------------------------------ */
export const InnerHero = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: ${sizes.spacing[32]}px;
  width: 100%;

  @media (min-width: ${breakpoints.lg}px) {
    gap: ${sizes.spacing[40]}px;
  }
`

/* ------------------------------------------------------------------ */
/* Text Container — title + subtitle                                   */
/* ------------------------------------------------------------------ */
export const TextContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${sizes.spacing[16]}px;
  width: 100%;
`

/* ------------------------------------------------------------------ */
/* Title row — title text + chevron decoration                         */
/* ------------------------------------------------------------------ */
export const TitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${sizes.spacing[8]}px;
  width: 100%;
`

export const PageTitle = styled(Typography)`
  color: ${colors.primitives.neutral600};
`

export const ChevronWrapper = styled.div`
  flex-shrink: 0;
  display: flex;
  align-items: center;

  [dir="ltr"] & svg {
    /* @noflip */
    transform: scaleX(-1);
  }
`

export const PageSubtitle = styled(Typography)`
  color: ${colors.light.text.primary};
  width: 100%;
`

/* ------------------------------------------------------------------ */
/* Card grid                                                           */
/* ------------------------------------------------------------------ */
export const CardGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: ${sizes.spacing[24]}px;
  width: 100%;

  @media (min-width: ${breakpoints.lg}px) {
    grid-template-columns: 1fr 1fr 1fr;
    gap: ${sizes.spacing[24]}px;
  }
`

/* ------------------------------------------------------------------ */
/* Swiper wrapper — mobile card carousel (EGDE-272).                   */
/* overflow:visible on .swiper lets the adjacent slides peek; the      */
/* wrapper clips the page so the peek doesn't cause horizontal scroll. */
/* ------------------------------------------------------------------ */
export const SwiperWrapper = styled.div`
  width: 100%;
  overflow: hidden;

  .swiper {
    width: 100%;
    overflow: visible;
  }

  .swiper-slide {
    height: auto;
  }
`

/* ------------------------------------------------------------------ */
/* Article card                                                        */
/* ------------------------------------------------------------------ */
export const ArticleCard = styled.a`
  display: flex;
  flex-direction: column;
  gap: ${sizes.spacing[16]}px;
  padding: ${sizes.spacing[16]}px;
  border: 1px solid ${colors.primitives.neutral300};
  border-radius: ${sizes.borderRadius[24]}px;
  background-color: ${colors.light.bg.primary};
  text-decoration: none;
  cursor: pointer;
  transition: border-color 150ms ease-in-out;

  &:hover {
    border-color: ${colors.primitives.neutral500};
  }

  @media (min-width: ${breakpoints.lg}px) {
    gap: ${sizes.spacing[24]}px;
  }
`

/* ------------------------------------------------------------------ */
/* Card image wrapper — with category badge overlay                    */
/* ------------------------------------------------------------------ */
export const CardImageWrapper = styled.div`
  position: relative;
  width: 100%;
  aspect-ratio: 352 / 220;
  border-radius: ${sizes.borderRadius[24]}px;
  overflow: hidden;

  /* EGDE-271: hover-only zoom. The image scales up while the card is hovered
     (pointer devices only — no hover on touch), clipped by overflow:hidden. */
  & img {
    transition: transform 300ms ease;
  }

  ${ArticleCard}:hover & img {
    transform: scale(1.05);
  }
`

/* EGDE-275: desktop / mobile image override wrappers. Both fill the image
   wrapper; CSS toggles which is shown so the CMS mobile override is honoured
   below the desktop breakpoint (the carousel viewport). */
export const DesktopImage = styled.div`
  position: absolute;
  inset: 0;
  display: none;

  @media (min-width: ${breakpoints.lg}px) {
    display: block;
  }
`

export const MobileImage = styled.div`
  position: absolute;
  inset: 0;
  display: block;

  @media (min-width: ${breakpoints.lg}px) {
    display: none;
  }
`

export const CategoryBadge = styled.div`
  position: absolute;
  /* EGDE-270: badge sits in the bottom corner on the reading-start side
     (RIGHT in RTL/Hebrew, LEFT in LTR). Use a logical inset so the side is
     correct in BOTH dev and prod builds. A physical right offset with a noflip
     directive regressed: the prod minifier strips noflip, so stylis-plugin-rtl
     then flips it to the left. */
  inset-block-end: ${sizes.spacing[16]}px;
  inset-inline-start: ${sizes.spacing[16]}px;
  background-color: rgba(255, 255, 255, 0.65);
  border-radius: ${sizes.borderRadius.full}px;
  padding: ${sizes.spacing[6]}px ${sizes.spacing[12]}px;
  backdrop-filter: blur(4px);
`

export const BadgeText = styled(Typography)`
  color: ${colors.primitives.neutral600};
  font-weight: 700 !important;
`

/* ------------------------------------------------------------------ */
/* Card content                                                        */
/* ------------------------------------------------------------------ */
export const CardContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${sizes.spacing[16]}px;
  width: 100%;
  flex: 1;
`

export const CardHeading = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${sizes.spacing[8]}px;
  width: 100%;
`

export const CardTitle = styled(Typography)`
  color: ${colors.light.text.primary};
  font-weight: 700 !important;
`

export const CardExcerpt = styled(Typography)`
  color: ${colors.light.text.primary};
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
`

/* ------------------------------------------------------------------ */
/* Card footer — CTA + date                                            */
/* Figma visual L→R: [date LEFT, CTA RIGHT]                           */
/* RTL: source [CTA, date] => visual [date LEFT, CTA RIGHT]           */
/* ------------------------------------------------------------------ */
export const CardFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  margin-top: auto;
`

export const CtaLink = styled.div`
  display: flex;
  align-items: center;
  gap: ${sizes.spacing[6]}px;
  margin-inline-start: auto;
`

export const CtaText = styled(Typography)`
  color: ${colors.light.text.primary};
  font-weight: 700 !important;
  white-space: nowrap;
`

export const CtaArrow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: ${colors.primitives.primary500};

  [dir="ltr"] & svg {
    /* @noflip */
    transform: scaleX(-1);
  }
`

export const DateText = styled(Typography)`
  color: ${colors.light.text.primary};
  white-space: nowrap;
`

/* ------------------------------------------------------------------ */
/* Pagination                                                          */
/* ------------------------------------------------------------------ */
export const PaginationContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${sizes.spacing[16]}px;
  width: 100%;

  /* EGDE-386: desktop grid pagination aligns to the start (right in RTL)
     edge of the cards container; mobile carousel stays centered */
  @media (min-width: ${breakpoints.lg}px) {
    justify-content: flex-start;
  }
`

export const PageButton = styled.button<{ $active: boolean }>`
  width: 32px;
  height: 32px;
  /* EGDE-433: never let the flex row squash the circles into ovals. */
  flex-shrink: 0;
  border-radius: ${sizes.borderRadius.full}px;
  border: 1px solid
    ${(p) =>
      p.$active ? colors.primitives.warning500 : colors.primitives.neutral300};
  background-color: ${(p) =>
    p.$active ? colors.primitives.secondary500 : colors.primitives.neutral0};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 0;
  transition:
    background-color 150ms ease-in-out,
    border-color 150ms ease-in-out,
    color 150ms ease-in-out;

  &:hover:not(:disabled) {
    border-color: ${colors.primitives.neutral500};
  }
`

export const PageButtonText = styled.span<{ $active: boolean }>`
  font-size: ${(p) => (p.$active ? 20 : 16)}px;
  font-weight: 500;
  line-height: 1;
  color: ${(p) =>
    p.$active ? colors.primitives.neutral600 : colors.primitives.neutral500};
`

export const PaginationArrow = styled.button`
  /* EGDE-496 r2: 32px like the FAQ pagination — the 20px hover disc hugged
     the glyph and read as "too small". */
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: none;
  cursor: pointer;
  padding: 0;
  border-radius: ${sizes.borderRadius.full}px;
  color: ${colors.primitives.neutral600};
  transition: background-color 0.2s ease;

  &:disabled {
    opacity: 0.3;
    cursor: default;
  }

  /* EGDE-496: the glyph is already black at rest, so hover paints the
     neutral200 disc behind it instead (neutral100 is indistinguishable from
     white). */
  &:hover:not(:disabled) {
    background-color: ${colors.primitives.neutral200};
  }
`
